// packages/ui/src/components/chat/ChatPresenceDot.tsx
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { TFunc } from '@tourtask/types/i18n';

interface ChatPresenceDotProps {
  online: boolean;
  t: TFunc;
  /** Override the default online/offline label. */
  label?: string;
  /** Hide the text and show only the dot. */
  dotOnly?: boolean;
  /** Render as an MUI Chip (a light, always-readable pill) instead of inline
   *  text — use on a colored header where plain colored text has poor contrast. */
  chip?: boolean;
}

/**
 * The "support online" green dot — server truth (docs/chat-architecture.md §3),
 * passed in as a boolean; this component never asserts presence itself.
 */
export function ChatPresenceDot({ online, t, label, dotOnly, chip }: ChatPresenceDotProps) {
  const text = label ?? (online ? t('Support_online') : t('Support_offline'));
  const dot = (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: online ? 'success.main' : 'grey.400',
        flexShrink: 0,
      }}
    />
  );

  if (chip) {
    return (
      <Chip
        size="small"
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {dot}
            {!dotOnly && <Box component="span">{text}</Box>}
          </Box>
        }
        sx={{
          height: 22,
          bgcolor: 'background.paper',
          color: online ? 'success.main' : 'text.secondary',
          fontWeight: 500,
          '& .MuiChip-label': { px: 1, fontSize: '0.72rem' },
        }}
      />
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {dot}
      {!dotOnly && (
        <Typography variant="caption" color={online ? 'success.main' : 'text.secondary'}>
          {text}
        </Typography>
      )}
    </Box>
  );
}
